/** @jsxImportSource @emotion/react */
import { TokenSelector } from "components/TokenSelector/TokenSelector";
import { fill, isEqual } from "lodash-es";
import percentRound from "percent-round";
import { memo, useEffect, useRef, useState } from "react";
import { GlobalStore } from "../../../store/global/GlobalStore";
import { MAX_SELECTABLE_TOKENS } from "config/consts";
import { AnimatePresence } from "framer-motion";
import { AmountBar } from "components/AmountBar/AmountBar";
import styled from "@emotion/styled";
import { Text } from "components/general/Text";
import { Spacer } from "components/general/Spacer/Spacer";
import { css } from "@emotion/react";
import { TradeLegMaker } from "components/TradeLeg/TradeLegMaker";
import RedoIconOriginal from "../../../img/redo.svg?react";
import { Button } from "components/general/Button/Button";
import { useTotalUSDAmount } from "hooks/useTotalUsdAmount";
import { Hash } from "viem";
import { useOnClickOutside } from "usehooks-ts";

const MakerSection = () => {
  const makerTokens = GlobalStore.useState(s => s.makerTokens);
  const takerTokens = GlobalStore.useState(s => s.takerTokens);
  const quoteMakerTokens = GlobalStore.useState(s => s.quote?.makerTokens);
  const totalUsdAmount = useTotalUSDAmount(makerTokens, true, quoteMakerTokens);
  const selectorRef = useRef<HTMLDivElement>(null);
  const [isDropdownDisplayed, setIsDropdownDisplayed] = useState(false);
  const [isWaitingToAddToken, setIsWaitingToAddToken] = useState(false);
  const [isChangingToken, setIsChangingToken] = useState<Hash | undefined>(undefined);
  const showSelector = makerTokens.length === 0 || isChangingToken || isWaitingToAddToken;

  useOnClickOutside(selectorRef, () => {
    setIsDropdownDisplayed(false);
    setIsChangingToken(undefined);
    setIsWaitingToAddToken(false);
  });

  useEffect(() => {
    // Remove token selector leg when tokens are removed or added
    setIsWaitingToAddToken(false);
    setIsChangingToken(undefined);
  }, [makerTokens]);

  const arePctsEquallySplit = isEqual(
    getRoundedPcts(makerTokens.length),
    makerTokens.map(e => e.pct)
  );

  return (
    <>
      {makerTokens.length > 1 && (
        <AmountBarContainer>
          <AmountBar counterParty="maker" totalUsdAmount={totalUsdAmount} />
          <AmountBarText>
            <ResetButton isHidden={arePctsEquallySplit} />
            <Text
              textStyle="Title"
              size="Medium"
              textCss={css`
                text-align: end;
                flex-grow: 1;
              `}
            >
              Total
              <Spacer as="span" width={8} />${totalUsdAmount.format(2)}
            </Text>
          </AmountBarText>
        </AmountBarContainer>
      )}
      <AnimatePresence>
        {/* Otherwise map through the existing list*/}
        {makerTokens.map((entry, index) => {
          if (isChangingToken === entry.tokenAddress) {
            return null;
          }
          const lastIndex = makerTokens.length - 1;
          const showPlusButton =
            index === lastIndex && takerTokens.length <= 1 && index !== MAX_SELECTABLE_TOKENS - 1;
          return (
            <TradeLegMaker
              key={entry.tokenAddress}
              tokenAddress={entry.tokenAddress}
              showPlusButton={showPlusButton}
              isWaitingToAddToken={isWaitingToAddToken}
              setIsWaitingToAddToken={setIsWaitingToAddToken}
              setIsChangingToken={setIsChangingToken}
            />
          );
        })}
      </AnimatePresence>
      {showSelector && (
        <TokenSelector
          counterParty="maker"
          isChangingToken={isChangingToken}
          isDropdownDisplayed={isDropdownDisplayed}
          setIsDropdownDisplayed={setIsDropdownDisplayed}
          ref={selectorRef}
        />
      )}
    </>
  );
};

// Sub-components

const AmountBarContainer = styled.div(({ theme }) => {
  return {
    display: "flex",
    flexDirection: "column",
    gap: 8,
    padding: "12px 26px",
    width: "100%",
    color: theme.font.secondary.default,
  };
});

const ResetButton = ({ isHidden }: { isHidden: boolean }) => {
  const makerTokensLength = GlobalStore.useState(s => s.makerTokens).length;

  return (
    <Button
      buttonType="quaternary"
      onClick={() => {
        GlobalStore.update(s => {
          const roundedPercentages = getRoundedPcts(makerTokensLength);
          s.makerTokens = s.makerTokens.map((entry, index) => {
            entry.pct = roundedPercentages[index];
            entry.isLocked = false;
            return entry;
          });
        });
      }}
      css={css`
        visibility: ${isHidden ? "hidden" : "visible"};
        display: flex;
        justify-content: center;
        align-items: center;
        gap: 5px;
        padding: 4px 13px 4px 4px;
      `}
    >
      <RedoIcon />
      <Text textStyle="Button" size="Small">
        Reset
      </Text>
    </Button>
  );
};

const RedoIcon = () => {
  return (
    <RedoIconContainer>
      <RedoIconOriginal width={14} height={14} />
    </RedoIconContainer>
  );
};

const RedoIconContainer = styled.div`
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const AmountBarText = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;

// Helpers

const getRoundedPcts = (makerTokensLength: number) => {
  // Create a new array and fill it with even values.
  const newEvenPercentages = fill(Array(makerTokensLength), 100 / makerTokensLength);
  // Round 33.3,33.3,33.3 to 34,33,33
  const roundedPercentages = percentRound(newEvenPercentages);
  return roundedPercentages;
};

export default memo(MakerSection);
